import type { DocItem, FormState } from './types.js'

const supportsFilePicker = typeof window !== 'undefined' && 'showOpenFilePicker' in window

export function getFileIcon(tipo: string): string {
  if (tipo.startsWith('image/')) return 'IMG'
  if (tipo === 'application/pdf') return 'PDF'
  if (tipo.includes('word') || tipo.includes('document')) return 'DOC'
  if (tipo.includes('sheet') || tipo.includes('excel')) return 'XLS'
  if (tipo.includes('zip') || tipo.includes('rar') || tipo.includes('7z')) return 'ZIP'
  return 'ARQ'
}

export function isImage(tipo: string) {
  return tipo.startsWith('image/')
}

export function isPdf(tipo: string) {
  return tipo === 'application/pdf'
}

export function formatBytes(bytes: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

async function pickFilesViaInput(multiple: boolean): Promise<File[]> {
  return new Promise((resolve) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.multiple = multiple
    input.accept = 'image/*,application/pdf,.doc,.docx,.xls,.xlsx,.txt,.zip'
    input.onchange = () => resolve(Array.from(input.files ?? []))
    input.oncancel = () => resolve([])
    input.click()
  })
}

export async function pickFiles(multiple: boolean): Promise<File[]> {
  if (supportsFilePicker) {
    try {
      const handles = await (
        window as Window &
          typeof globalThis & {
            showOpenFilePicker: (opts: object) => Promise<{ getFile(): Promise<File> }[]>
          }
      ).showOpenFilePicker({
        multiple,
        types: [
          {
            description: 'Documentos e imagens',
            accept: {
              'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
              'application/pdf': ['.pdf'],
              'application/msword': ['.doc'],
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
              'text/plain': ['.txt'],
            },
          },
        ],
      })
      return Promise.all(handles.map((h) => h.getFile()))
    } catch (e) {
      if ((e as Error).name === 'AbortError') return []
      return pickFilesViaInput(multiple)
    }
  }
  return pickFilesViaInput(multiple)
}

export function fileToFormPatch(file: File): Partial<FormState> {
  return {
    nome: file.name,
    tipo: file.type || 'application/octet-stream',
    tamanho: file.size,
    arquivo: file,
    previewObjectUrl: URL.createObjectURL(file),
    hasExistingArquivo: true,
  }
}

export function docToFormState(doc: DocItem): FormState {
  return {
    nome: doc.nome,
    categoria: doc.categoria,
    data: doc.data,
    fornecedor: doc.fornecedor ?? '',
    valor: doc.valor != null ? String(doc.valor) : '',
    observacoes: doc.observacoes ?? '',
    moduloId: doc.moduloId ?? '',
    lancamentoId: doc.lancamentoId != null ? String(doc.lancamentoId) : '',
    tipo: doc.tipo,
    tamanho: doc.tamanho ?? 0,
    arquivo: null,
    previewObjectUrl: '',
    hasExistingArquivo: Boolean(doc.hasArquivo),
    sourceDocId: doc.id,
  }
}

export function revokePreviewObjectUrl(url: string) {
  if (url.startsWith('blob:')) URL.revokeObjectURL(url)
}
